import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    signal
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    AutocompleteComponent,
    AutocompleteOptionComponent,
    CheckboxComponent,
    InputComponent,
    OptionComponent,
    SelectComponent,
    SnackbarService
} from '@siemens/component-lib';
import { catchError, map, Observable, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
    participantNotRequired,
    WorkflowConfigField,
    WorkflowConfigFieldInput,
    WorkflowConfigFieldRole,
    WorkflowConfigFieldSelect,
    WorkflowConfigFieldType,
    WorkflowConfigRoleAssignmentType,
    formatGermanDecimalValue,
    getEffectiveInputType,
    isNumericInputType,
    parseGermanDecimalValue
} from '@monorepo/interfaces';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ControlErrorHandlerPipe, ControlErrorParamsPipe } from '../../pipes/control-error-handler.pipe';
import { GraphService } from '../../../auth/services/graph.service';
import { EntraIdGroupMember } from '../../interfaces/admin/EntraIdGroupMember';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CreateWorkflowOperations } from '../../../workflows/create-workflow/create-workflow.operations';
import { MessageOperationType } from '../../enums/MessageOperationType.enum';
import { IsFieldLoadingPipe } from '../../pipes/options-field-loading.pipe';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { GckUserAssignmentService } from '../../services/gck-user-assignment.service';
import { WorkflowFormControlService } from '../../services/workflow-form-control.service';
import { WorkflowOptionSetService } from '../../services/workflow-option-set.service';
import { mapFieldNameToRole } from '../../utils/role-field-mapping';
import { Roles } from '@monorepo/interfaces';

@Component({
    selector: 'app-workflow-field',
    imports: [
        ReactiveFormsModule,
        InputComponent,
        SelectComponent,
        OptionComponent,
        TranslatePipe,
        ControlErrorHandlerPipe,
        ControlErrorParamsPipe,
        AutocompleteComponent,
        AutocompleteOptionComponent,
        CheckboxComponent,
        IsFieldLoadingPipe,
        LoadingIndicatorComponent
    ],
    templateUrl: './workflow-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowFieldComponent implements OnInit {
    private readonly graphService = inject(GraphService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly snackbarService = inject(SnackbarService);
    private readonly translateService = inject(TranslateService);
    private readonly gckUserAssignmentService = inject(GckUserAssignmentService);
    private readonly workflowFormControlService = inject(WorkflowFormControlService);
    private readonly workflowOptionSetService = inject(WorkflowOptionSetService);

    constructor() {
        effect(onCleanup => {
            const field = this.field();
            const control = this.control();
            if (
                field.fieldType !== WorkflowConfigFieldType.INPUT ||
                !isNumericInputType(getEffectiveInputType(field))
            ) {
                return;
            }

            this.syncNumericDisplayControl(control);
            const subscriptions = new Subscription();
            subscriptions.add(
                control.valueChanges.subscribe(value => {
                    this.numericDisplayControl.setValue(formatGermanDecimalValue(value), { emitEvent: false });
                })
            );
            subscriptions.add(
                control.statusChanges.subscribe(() => {
                    if (control.disabled && this.numericDisplayControl.enabled) {
                        this.numericDisplayControl.disable({ emitEvent: false });
                    } else if (control.enabled && this.numericDisplayControl.disabled) {
                        this.numericDisplayControl.enable({ emitEvent: false });
                    }
                })
            );
            subscriptions.add(
                this.numericDisplayControl.valueChanges.subscribe(value => {
                    control.markAsDirty();
                    control.setValue(parseGermanDecimalValue(value) ?? value);
                })
            );
            onCleanup(() => subscriptions.unsubscribe());
        });
    }

    public readonly field = input.required<WorkflowConfigField>();
    public readonly control = input.required<FormControl>();

    protected entraIdGroupMembers = signal<EntraIdGroupMember[]>([]);
    private allEntraIdGroupMembers = signal<EntraIdGroupMember[]>([]);
    protected isEntraIdGroupMembersLoading = signal<boolean>(false);
    protected readonly participantNotRequired = participantNotRequired;

    protected readonly isInputField = computed(() => this.field().fieldType === WorkflowConfigFieldType.INPUT);
    protected readonly isInputFieldCheckbox = computed(
        () =>
            this.field().fieldType === WorkflowConfigFieldType.INPUT &&
            (this.field() as WorkflowConfigFieldInput).type === 'boolean'
    );

    protected readonly isSelectField = computed(() => this.field().fieldType === WorkflowConfigFieldType.SELECT);

    protected readonly isDateRangeField = computed(() => this.field().fieldType === WorkflowConfigFieldType.DATE_RANGE);

    protected readonly isRoleField = computed(() => this.field().fieldType === WorkflowConfigFieldType.ROLE);
    protected readonly isRequiredRoleField = computed(
        () => (this.field() as WorkflowConfigFieldRole).roleType === WorkflowConfigRoleAssignmentType.REQUIRED
    );

    protected readonly inputType = computed(() => {
        if (this.isInputField()) {
            return this.isNumericField() ? 'text' : (this.field() as WorkflowConfigFieldInput).type;
        }
        return 'text';
    });

    protected readonly isNumericField = computed(() => {
        const field = this.field();
        if (field.fieldType !== WorkflowConfigFieldType.INPUT) return false;
        return isNumericInputType(getEffectiveInputType(field));
    });

    protected readonly numericDisplayControl = new FormControl('', { nonNullable: true });

    protected readonly selectOptions = computed(() => {
        if (this.isSelectField()) {
            // Check if dynamic options are available in the service first
            const dynamicOptions = this.workflowOptionSetService.getFieldOptions(this.field().name);
            if (dynamicOptions !== undefined) {
                return dynamicOptions;
            }
            // Fall back to static options from config
            return (this.field() as WorkflowConfigFieldSelect).options || [];
        }
        return [];
    });

    ngOnInit(): void {
        this.setControlValueToNullIfDateRange();
        this.loadEntraIdGroupMembersIfFieldTypeRole();
    }

    private syncNumericDisplayControl(control: FormControl): void {
        this.numericDisplayControl.setValue(formatGermanDecimalValue(control.value), { emitEvent: false });
        if (control.disabled && this.numericDisplayControl.enabled) {
            this.numericDisplayControl.disable({ emitEvent: false });
        } else if (control.enabled && this.numericDisplayControl.disabled) {
            this.numericDisplayControl.enable({ emitEvent: false });
        }
    }

    private loadEntraIdGroupMembersIfFieldTypeRole() {
        if (this.isRoleField() && this.control().value !== participantNotRequired) {
            const roleField = this.field() as WorkflowConfigFieldRole;
            if (roleField.groupEntraId) {
                this.loadRolesFromEntraIdGroup(roleField.groupEntraId);
            }
        }
    }

    private loadRolesFromEntraIdGroup(groupEntraId: string): void {
        this.isEntraIdGroupMembersLoading.set(true);
        this.graphService
            .getGroupMembers(groupEntraId)
            .pipe(
                tap(response => {
                    const members = response.value || [];
                    this.allEntraIdGroupMembers.set(members);
                    this.entraIdGroupMembers.set(members);
                }),
                switchMap(() => {
                    const gckControl = this.workflowFormControlService.findGckControl(this.control());
                    if (!gckControl?.value) {
                        return of(this.allEntraIdGroupMembers());
                    }
                    const roleEnum = this.isRoleField() ? mapFieldNameToRole(this.field().name) : undefined;
                    return this.filterMembersByGck(gckControl.value as string, roleEnum);
                }),
                tap(members => this.entraIdGroupMembers.set(members)),
                tap(() => this.isEntraIdGroupMembersLoading.set(false)),
                tap(() => this.setupGckListenerIfRoleField()),
                catchError(() => this.showErrorMessage()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private setupGckListenerIfRoleField(): void {
        if (!this.isRoleField()) return;
        const gckControl = this.workflowFormControlService.findGckControl(this.control());
        if (!gckControl) return;

        gckControl.valueChanges
            .pipe(
                startWith(gckControl.value),
                distinctUntilChanged(),
                switchMap(gckValue => {
                    if (!gckValue) {
                        return of(this.allEntraIdGroupMembers());
                    }
                    const roleEnum = mapFieldNameToRole(this.field().name);
                    return this.filterMembersByGck(gckValue as string, roleEnum);
                }),
                tap(members => {
                    const currentValue = this.control().value as string | null;
                    if (currentValue && currentValue !== this.participantNotRequired) {
                        if (!members.some(m => m.id === currentValue)) {
                            this.control().setValue(null);
                        }
                    }
                    this.entraIdGroupMembers.set(members);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private filterMembersByGck(gckValue: string, role?: Roles): Observable<EntraIdGroupMember[]> {
        return this.gckUserAssignmentService.getUsersByGck(gckValue, role).pipe(
            map(assignments => {
                // Fallback: if user-service returns empty array, use all Entra members
                if (assignments.length === 0) {
                    return this.allEntraIdGroupMembers();
                }
                // Filter Entra members by user-service OIDs
                const assignedOids = new Set(assignments.map(a => a.userOid));
                return this.allEntraIdGroupMembers().filter(m => assignedOids.has(m.id));
            }),
            catchError(() => of(this.allEntraIdGroupMembers()))
        );
    }

    private showErrorMessage(): Observable<boolean> {
        this.snackbarService.open(
            CreateWorkflowOperations.getSnackbarMessageConfig(
                MessageOperationType.ERROR,
                'workflow.error.failed-to-load-entra-group-members.title',
                'workflow.error.failed-to-load-entra-group-members.description',
                this.translateService
            )
        );
        return of(false);
    }

    private setControlValueToNullIfDateRange(): void {
        if (this.isDateRangeField() && this.control().value === '') {
            this.control().setValue(null);
        }
    }
}
